import { useEffect, useRef, useState } from "react";
import { X, Download, Share2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { MODE_MAP } from "@/lib/modes";

/** Draws a stylised Jixxeo share card onto a canvas and returns a Blob. */
async function renderCard(canvas, { scan }) {
  const W = 1080, H = 1920;
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext("2d");

  // Obsidian background with vignette
  const bg = ctx.createLinearGradient(0, 0, 0, H);
  bg.addColorStop(0, "#0F141C");
  bg.addColorStop(1, "#07090E");
  ctx.fillStyle = bg; ctx.fillRect(0, 0, W, H);

  // Grid lines
  ctx.strokeStyle = "rgba(0,229,255,0.08)"; ctx.lineWidth = 2;
  for (let x = 0; x < W; x += 90) { ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, H); ctx.stroke(); }
  for (let y = 0; y < H; y += 90) { ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(W, y); ctx.stroke(); }

  const mode = MODE_MAP[scan.mode] || MODE_MAP.jixxeo_lens;

  // Thumbnail card
  if (scan.thumbnail) {
    const img = await loadImage(scan.thumbnail);
    const pad = 60;
    const iw = W - pad * 2;
    const ih = Math.min(1100, (img.height / img.width) * iw);
    const ix = pad; const iy = 240;
    roundRect(ctx, ix, iy, iw, ih, 48);
    ctx.save(); ctx.clip();
    // cover-fit
    const srcRatio = img.width / img.height;
    const dstRatio = iw / ih;
    let sw = img.width, sh = img.height, sx = 0, sy = 0;
    if (srcRatio > dstRatio) { sw = img.height * dstRatio; sx = (img.width - sw) / 2; }
    else { sh = img.width / dstRatio; sy = (img.height - sh) / 2; }
    ctx.drawImage(img, sx, sy, sw, sh, ix, iy, iw, ih);
    ctx.restore();
    // Border
    ctx.strokeStyle = "rgba(255,255,255,0.1)"; ctx.lineWidth = 3;
    roundRect(ctx, ix, iy, iw, ih, 48); ctx.stroke();
  }

  // Top brand row
  ctx.fillStyle = mode.color;
  ctx.beginPath(); ctx.arc(90, 120, 30, 0, Math.PI * 2); ctx.fill();
  ctx.fillStyle = "#07090E";
  ctx.beginPath(); ctx.arc(90, 120, 14, 0, Math.PI * 2); ctx.fill();

  ctx.fillStyle = "#FFFFFF";
  ctx.font = "900 56px Outfit, system-ui, sans-serif";
  ctx.textBaseline = "middle";
  ctx.fillText("Jixxeo Scan", 140, 120);

  ctx.fillStyle = "#94A3B8";
  ctx.font = "500 28px 'JetBrains Mono', monospace";
  ctx.fillText(mode.label.toUpperCase() + " · " + scan.confidence.toFixed(0) + "% MATCH", 140, 180);

  // Title block (below image)
  const titleY = 1400;
  ctx.fillStyle = "#FFFFFF";
  ctx.font = "900 92px Outfit, system-ui, sans-serif";
  wrapText(ctx, scan.title, 60, titleY, W - 120, 100);

  ctx.fillStyle = "#CBD5E1";
  ctx.font = "400 38px 'IBM Plex Sans', system-ui, sans-serif";
  wrapText(ctx, scan.summary, 60, titleY + 180, W - 120, 50, 4);

  // Watermark footer
  const footerY = H - 180;
  ctx.fillStyle = "rgba(0,229,255,0.12)";
  roundRect(ctx, 60, footerY - 40, W - 120, 140, 36); ctx.fill();
  ctx.strokeStyle = "rgba(0,229,255,0.3)"; ctx.lineWidth = 2;
  roundRect(ctx, 60, footerY - 40, W - 120, 140, 36); ctx.stroke();

  ctx.fillStyle = "#00E5FF";
  ctx.font = "700 36px 'JetBrains Mono', monospace";
  ctx.textBaseline = "middle";
  ctx.fillText("SEE IT. TAP IT. KNOW IT.", 110, footerY + 10);

  ctx.fillStyle = "#94A3B8";
  ctx.font = "500 30px 'IBM Plex Sans', system-ui, sans-serif";
  ctx.fillText("Scanned with Jixxeo AI · jixxeoscan.com", 110, footerY + 60);

  return new Promise((res) => canvas.toBlob(res, "image/png", 0.95));
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const i = new Image();
    i.crossOrigin = "anonymous";
    i.onload = () => resolve(i);
    i.onerror = reject;
    i.src = src;
  });
}

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

function wrapText(ctx, text, x, y, maxW, lineH, maxLines = 2) {
  if (!text) return;
  const words = text.split(" ");
  let line = "", lines = [];
  for (const w of words) {
    const t = line ? line + " " + w : w;
    if (ctx.measureText(t).width > maxW) { lines.push(line); line = w; }
    else { line = t; }
    if (lines.length >= maxLines) break;
  }
  if (line && lines.length < maxLines) lines.push(line);
  lines.slice(0, maxLines).forEach((l, i) => ctx.fillText(l, x, y + i * lineH));
}

export default function ShareCardStudio({ scan, onClose }) {
  const canvasRef = useRef(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [blob, setBlob] = useState(null);
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    (async () => {
      setBusy(true);
      try {
        const b = await renderCard(canvasRef.current, { scan });
        setBlob(b);
        setPreviewUrl(URL.createObjectURL(b));
      } catch (e) {
        toast.error("Could not render share card");
      } finally {
        setBusy(false);
      }
    })();
    return () => { if (previewUrl) URL.revokeObjectURL(previewUrl); };
    // eslint-disable-next-line
  }, [scan?.id]);

  const download = () => {
    if (!blob) return;
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `jixxeo-${(scan.title || "scan").toLowerCase().replace(/[^a-z0-9]+/gi, "-")}.png`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 1000);
    toast.success("Share card downloaded");
  };

  const share = async () => {
    if (!blob) return;
    const file = new File([blob], "jixxeo-share.png", { type: "image/png" });
    const text = `I scanned "${scan.title}" with Jixxeo Scan — Point. Tap. Discover. jixxeoscan.com`;
    try {
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ title: "Jixxeo Scan", text, files: [file] });
      } else if (navigator.share) {
        await navigator.share({ title: "Jixxeo Scan", text });
      } else {
        await navigator.clipboard.writeText(text);
        toast.success("Share text copied");
      }
    } catch { /* user cancelled */ }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-testid="share-card-studio">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-md" onClick={onClose} />
      <div className="relative w-full max-w-sm jx-glass rounded-3xl p-4 jx-fade-up">
        <div className="flex items-center justify-between mb-3">
          <p className="font-heading font-bold">Share Card Studio</p>
          <button data-testid="share-studio-close" onClick={onClose} className="text-slate-400 p-1"><X size={20} /></button>
        </div>

        <div className="aspect-[9/16] w-full rounded-2xl overflow-hidden border border-white/10 bg-slate-900 flex items-center justify-center">
          {busy && <Loader2 className="animate-spin text-cyan-300" />}
          {previewUrl && <img src={previewUrl} alt="Jixxeo share card" className="w-full h-full object-contain" />}
        </div>
        <canvas ref={canvasRef} className="hidden" />

        <div className="mt-4 grid grid-cols-2 gap-2">
          <button data-testid="share-studio-download" disabled={busy} onClick={download}
            className="h-11 rounded-xl bg-white/5 border border-white/10 inline-flex items-center justify-center gap-2 active:scale-95 disabled:opacity-50">
            <Download size={16} /> Download
          </button>
          <button data-testid="share-studio-share" disabled={busy} onClick={share}
            className="h-11 rounded-xl bg-cyan-400 text-slate-950 font-semibold inline-flex items-center justify-center gap-2 active:scale-95 disabled:opacity-50">
            <Share2 size={16} /> Share
          </button>
        </div>
        <p className="mt-3 text-center text-[10px] font-mono-data uppercase tracking-widest text-slate-500">
          Jixxeo watermark · jixxeoscan.com
        </p>
      </div>
    </div>
  );
}
